/**
 * src/components/dashboard/BudgetRingsCard.tsx
 * Ultra-Premium Apple Watch Activity Concentric Rings for Spending Budgets
 * Enhanced with luminous gradients, glowing tracks, adaptive Dark/Light styling,
 * category icons, and audio-haptic feedback.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { api, type TransactionItem } from '../../services/api';
import { Sliders, Coffee, Utensils, Zap, Check } from 'lucide-react';
import { GlassModal } from '../ui/GlassModal';
import { haptic, playTapSound, playSuccessSound } from '../../utils/audioHaptics';

interface BudgetRingsCardProps {
  transactions: TransactionItem[];
}

interface BudgetSettings {
  totalBudget: number;
  teaBudget: number;
  foodBudget: number;
}

export const BudgetRingsCard: React.FC<BudgetRingsCardProps> = ({ transactions }) => {
  const [budgets, setBudgets] = useState<BudgetSettings>(() => {
    try {
      const saved = localStorage.getItem('ckaudit_budget_targets');
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      totalBudget: 25000,
      teaBudget: 5000,
      foodBudget: 10000
    };
  });

  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [tempBudgets, setTempBudgets] = useState<BudgetSettings>(budgets);
  const [monthTransactions, setMonthTransactions] = useState<TransactionItem[]>([]);
  const [isLoadingMonth, setIsLoadingMonth] = useState(false);

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const currentMonthName = now.toLocaleString('default', { month: 'long' });

  // Fetch true month-to-date transactions from database
  useEffect(() => {
    let isMounted = true;
    const fetchCurrentMonth = async () => {
      setIsLoadingMonth(true);
      try {
        const firstDay = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;
        const lastDayNum = new Date(currentYear, currentMonth + 1, 0).getDate();
        const lastDay = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(lastDayNum).padStart(2, '0')}`;

        const res = await api.transactions.list({
          startDate: firstDay,
          endDate: lastDay,
          type: 'paid',
          limit: 200
        });

        if (isMounted && res.transactions) {
          setMonthTransactions(res.transactions);
        }
      } catch (err) {
        console.warn('Could not fetch month transactions for budget rings', err);
      } finally {
        if (isMounted) setIsLoadingMonth(false);
      }
    };

    fetchCurrentMonth();
    return () => { isMounted = false; };
  }, [transactions.length, currentYear, currentMonth]);

  // Compute spend strictly for the current calendar month
  const monthlySpend = useMemo(() => {
    let total = 0;
    let tea = 0;
    let food = 0;

    const source = monthTransactions.length > 0
      ? monthTransactions
      : transactions.filter(t => {
          if (!t.transaction_date) return false;
          const d = new Date(t.transaction_date.replace(' ', 'T'));
          return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
        });

    for (const t of source) {
      if (t.transaction_type === 'paid') {
        const cat = (t.category_name || '').toLowerCase();
        const desc = (t.description || '').toLowerCase();
        total += t.amount;
        if (
          cat.includes('tea') || cat.includes('soda') || cat.includes('snack') || cat.includes('coffee') || cat.includes('chai') ||
          desc.includes('tea') || desc.includes('soda') || desc.includes('chai') || desc.includes('coffee')
        ) {
          tea += t.amount;
        } else if (
          cat.includes('food') || cat.includes('meal') || cat.includes('dinner') || cat.includes('lunch') || cat.includes('vadapav') ||
          desc.includes('food') || desc.includes('lunch') || desc.includes('dinner') || desc.includes('meal') || desc.includes('vadapav')
        ) {
          food += t.amount;
        }
      }
    }

    return { total, tea, food, count: source.length };
  }, [monthTransactions, transactions, currentYear, currentMonth]);

  // Calculate percentages (capped at 100 for clean visualization)
  const totalPercent = Math.min(Math.round((monthlySpend.total / budgets.totalBudget) * 100), 100);
  const teaPercent = Math.min(Math.round((monthlySpend.tea / budgets.teaBudget) * 100), 100);
  const foodPercent = Math.min(Math.round((monthlySpend.food / budgets.foodBudget) * 100), 100);

  // Concentric circle geometry
  const size = 186;
  const center = size / 2;
  const strokeWidth = 14;

  const r1 = 78; // outer (Total / Rose)
  const r2 = 60; // middle (Tea / Lime)
  const r3 = 42; // inner (Food / Cyan)

  const c1 = 2 * Math.PI * r1;
  const c2 = 2 * Math.PI * r2;
  const c3 = 2 * Math.PI * r3;

  const offset1 = c1 - (totalPercent / 100) * c1;
  const offset2 = c2 - (teaPercent / 100) * c2;
  const offset3 = c3 - (foodPercent / 100) * c3;

  const handleOpenConfig = () => {
    haptic.tap();
    playTapSound();
    setTempBudgets(budgets);
    setIsConfigOpen(true);
  };

  const handleSaveBudgets = () => {
    haptic.success();
    playSuccessSound();
    setBudgets(tempBudgets);
    localStorage.setItem('ckaudit_budget_targets', JSON.stringify(tempBudgets));
    setIsConfigOpen(false);
  };

  return (
    <div
      className="apple-card"
      style={{
        padding: '24px 24px',
        borderRadius: '26px',
        position: 'relative'
      }}
    >
      {/* Top Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
        <div>
          <div className="apple-card-subtitle" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>Activity & Velocity</span>
            <span>•</span>
            <span style={{ color: 'var(--ios-blue)', fontWeight: 600 }}>
              {monthlySpend.count > 0 ? `${monthlySpend.count} Entries` : 'Month-to-Date'}
            </span>
            {isLoadingMonth && <span style={{ opacity: 0.6 }}>• Refreshing...</span>}
          </div>
          <div className="apple-card-title" style={{ fontSize: '1.25rem', marginTop: '3px' }}>
            {currentMonthName} Spending Rings
          </div>
        </div>

        <button
          type="button"
          onClick={handleOpenConfig}
          className="ios-tap-target"
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'var(--surface-glass)',
            border: 'var(--border-hairline)',
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all var(--duration-fast)'
          }}
          title="Configure Monthly Budgets"
        >
          <Sliders size={16} strokeWidth={2.2} />
        </button>
      </div>

      {/* Main Content: Concentric Rings + Stats Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
        {/* Apple Watch Activity Rings (SVG with glowing gradients) */}
        <div style={{ width: size, height: size, flexShrink: 0, position: 'relative' }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
            <defs>
              {/* Outer Ring Gradient: Electric Crimson to Magenta */}
              <linearGradient id="ringTotalGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ff2d55" />
                <stop offset="100%" stopColor="#ff375f" />
              </linearGradient>

              {/* Middle Ring Gradient: Apple Spring Mint */}
              <linearGradient id="ringTeaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#30d158" />
                <stop offset="100%" stopColor="#34c759" />
              </linearGradient>

              {/* Inner Ring Gradient: Electric Cyan */}
              <linearGradient id="ringFoodGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#0a84ff" />
                <stop offset="100%" stopColor="#64d2ff" />
              </linearGradient>
            </defs>

            {/* Background Inactive Tracks */}
            <circle cx={center} cy={center} r={r1} fill="none" stroke="rgba(255, 45, 85, 0.18)" strokeWidth={strokeWidth} />
            <circle cx={center} cy={center} r={r2} fill="none" stroke="rgba(48, 209, 88, 0.18)" strokeWidth={strokeWidth} />
            <circle cx={center} cy={center} r={r3} fill="none" stroke="rgba(10, 132, 255, 0.18)" strokeWidth={strokeWidth} />

            {/* Outer Ring: Total Expense */}
            <circle
              cx={center}
              cy={center}
              r={r1}
              fill="none"
              stroke="url(#ringTotalGrad)"
              strokeWidth={strokeWidth}
              strokeDasharray={c1}
              strokeDashoffset={offset1}
              strokeLinecap="round"
              style={{ 
                transition: 'stroke-dashoffset 0.9s cubic-bezier(0.32, 0.72, 0, 1)',
                filter: 'drop-shadow(0 0 4px rgba(255, 45, 85, 0.5))'
              }}
            />

            {/* Middle Ring: Tea, Soda & Snacks */}
            <circle
              cx={center}
              cy={center}
              r={r2}
              fill="none"
              stroke="url(#ringTeaGrad)"
              strokeWidth={strokeWidth}
              strokeDasharray={c2}
              strokeDashoffset={offset2}
              strokeLinecap="round"
              style={{ 
                transition: 'stroke-dashoffset 0.9s cubic-bezier(0.32, 0.72, 0, 1) 0.1s',
                filter: 'drop-shadow(0 0 4px rgba(48, 209, 88, 0.5))'
              }}
            />

            {/* Inner Ring: Food & Meals */}
            <circle
              cx={center}
              cy={center}
              r={r3}
              fill="none"
              stroke="url(#ringFoodGrad)"
              strokeWidth={strokeWidth}
              strokeDasharray={c3}
              strokeDashoffset={offset3}
              strokeLinecap="round"
              style={{ 
                transition: 'stroke-dashoffset 0.9s cubic-bezier(0.32, 0.72, 0, 1) 0.2s',
                filter: 'drop-shadow(0 0 4px rgba(10, 132, 255, 0.5))'
              }}
            />
          </svg>

          {/* Central Percentage Display */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}
          >
            <span style={{ 
              fontSize: '1.45rem', 
              fontWeight: 800, 
              color: 'var(--text-primary)', 
              fontFamily: 'var(--font-numeric)',
              lineHeight: 1
            }}>
              {totalPercent}%
            </span>
            <span style={{ 
              fontSize: '0.625rem', 
              color: 'var(--text-tertiary)', 
              textTransform: 'uppercase', 
              fontWeight: 700, 
              letterSpacing: '0.06em',
              marginTop: '2px'
            }}>
              Burn Rate
            </span>
          </div>
        </div>

        {/* Stats Legend: Clean High-Contrast Glass Rows */}
        <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Item 1: Total */}
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              padding: '10px 14px', 
              borderRadius: '14px', 
              background: 'var(--surface-glass)',
              border: 'var(--border-hairline)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div 
                style={{ 
                  width: '28px', 
                  height: '28px', 
                  borderRadius: '8px', 
                  background: 'rgba(255, 45, 85, 0.16)', 
                  color: '#ff2d55',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Zap size={15} strokeWidth={2.4} />
              </div>
              <div>
                <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  Total Spending
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                  ₹{monthlySpend.total.toLocaleString('en-IN')} of ₹{budgets.totalBudget.toLocaleString('en-IN')}
                </div>
              </div>
            </div>
            <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#ff2d55', fontFamily: 'var(--font-numeric)' }}>
              {totalPercent}%
            </span>
          </div>

          {/* Item 2: Tea & Snacks */}
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              padding: '10px 14px', 
              borderRadius: '14px', 
              background: 'var(--surface-glass)',
              border: 'var(--border-hairline)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div 
                style={{ 
                  width: '28px', 
                  height: '28px', 
                  borderRadius: '8px', 
                  background: 'rgba(48, 209, 88, 0.16)', 
                  color: 'var(--ios-green)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Coffee size={15} strokeWidth={2.4} />
              </div>
              <div>
                <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  Tea & Snacks
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                  ₹{monthlySpend.tea.toLocaleString('en-IN')} of ₹{budgets.teaBudget.toLocaleString('en-IN')}
                </div>
              </div>
            </div>
            <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--ios-green)', fontFamily: 'var(--font-numeric)' }}>
              {teaPercent}%
            </span>
          </div>

          {/* Item 3: Food & Meals */}
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              padding: '10px 14px', 
              borderRadius: '14px', 
              background: 'var(--surface-glass)',
              border: 'var(--border-hairline)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div 
                style={{ 
                  width: '28px', 
                  height: '28px', 
                  borderRadius: '8px', 
                  background: 'rgba(10, 132, 255, 0.16)', 
                  color: 'var(--ios-blue)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Utensils size={15} strokeWidth={2.4} />
              </div>
              <div>
                <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  Food & Meals
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                  ₹{monthlySpend.food.toLocaleString('en-IN')} of ₹{budgets.foodBudget.toLocaleString('en-IN')}
                </div>
              </div>
            </div>
            <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--ios-blue)', fontFamily: 'var(--font-numeric)' }}>
              {foodPercent}%
            </span>
          </div>
        </div>
      </div>

      {/* Budget Configuration Sheet */}
      {isConfigOpen && (
        <GlassModal
          isOpen={isConfigOpen}
          onClose={() => setIsConfigOpen(false)}
          title="Configure Budget Targets"
          maxWidth="440px"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="ios-inset-group" style={{ margin: 0 }}>
              <div style={{ padding: '12px 16px' }}>
                <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                  Total Monthly Budget Cap (₹)
                </label>
                <input
                  type="number"
                  value={tempBudgets.totalBudget}
                  onChange={(e) => setTempBudgets(prev => ({ ...prev, totalBudget: Number(e.target.value) || 0 }))}
                  style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 700 }}
                />
              </div>

              <div style={{ padding: '12px 16px' }}>
                <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                  Tea, Soda & Snacks Cap (₹)
                </label>
                <input
                  type="number"
                  value={tempBudgets.teaBudget}
                  onChange={(e) => setTempBudgets(prev => ({ ...prev, teaBudget: Number(e.target.value) || 0 }))}
                  style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 700 }}
                />
              </div>

              <div style={{ padding: '12px 16px' }}>
                <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                  Food & Meals Cap (₹)
                </label>
                <input
                  type="number"
                  value={tempBudgets.foodBudget}
                  onChange={(e) => setTempBudgets(prev => ({ ...prev, foodBudget: Number(e.target.value) || 0 }))}
                  style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 700 }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => {
                  haptic.tap();
                  playTapSound();
                  setIsConfigOpen(false);
                }}
                className="ios-tap-target"
                style={{ flex: 1, padding: '12px', borderRadius: 'var(--radius-pill)', border: 'var(--border-hairline)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveBudgets}
                className="ios-primary-button ios-tap-target"
                style={{ flex: 2, padding: '12px' }}
              >
                <Check size={16} />
                <span>Save Limits</span>
              </button>
            </div>
          </div>
        </GlassModal>
      )}
    </div>
  );
};
